import { useMutation, useQueryClient } from "react-query";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IAttachment } from "../../common/models/IAttachment"
import AttachmentsDataService from "../../services/attachments-data-service/AttachmentsDataService";
import { IObjectAttachmentsModel } from "../../services/attachments-data-service/Models";
import { RestError } from "../../services/rest-service/RestService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";

export type UseDeleteAttachmentQueryParams = {
    groupID: number,
    eLynxObject: IeLynxObject
} & BaseUseMutationParameters<void, IAttachment>;

export function useDeleteAttachment(params: UseDeleteAttachmentQueryParams) {
    const queryClient = useQueryClient();

    return useMutation<void, RestError, IAttachment, MutationContextType>(
        (attachment) => {
            const attachmentModel = attachment as IAttachment;

            return AttachmentsDataService.deleteAttachment(
                params.groupID,
                params.eLynxObject,
                attachmentModel,
            );
        },
        {
            onMutate: async (attachment: IAttachment) => {
                await queryClient.cancelQueries(keys.all);

                const previousAttachments = queryClient.getQueryData<IObjectAttachmentsModel>(
                    keys.attachments(
                        params.groupID,
                        params.eLynxObject
                    )
                );

                queryClient.setQueryData<IObjectAttachmentsModel>(
                    keys.attachments(
                        params.groupID,
                        params.eLynxObject
                    ),
                    (old?: IObjectAttachmentsModel) => {
                        return {
                            canEdit: old?.canEdit,
                            attachments: old?.attachments?.filter(a => a.id !== attachment.id) ?? []
                        } as IObjectAttachmentsModel;
                    }
                );

                return {
                    previousAttachments
                };
            },
            onError: params.onError,
            onSuccess: params.onSuccess,
            onSettled: (_data, error, _attachment, context) => {
                if (error && context?.previousAttachments) {
                    queryClient.setQueryData<IObjectAttachmentsModel>(
                        keys.attachments(
                            params.groupID,
                            params.eLynxObject
                        ),
                        context?.previousAttachments
                    );
                }

                queryClient.invalidateQueries(keys.attachments(
                    params.groupID,
                    params.eLynxObject
                ));
            }
        }
    );
}